// Options for the shell's Simulation and Course filter chips (BD-1284).
//
// The chip lists what was sent in the selected dates to the selected tenancies,
// as the old (QuickSight) reports did. Everything else ever sent to a tenancy
// the viewer can see sits in a second, collapsed section of the same dropdown,
// so a sim the customer knows exists is one click away rather than missing.
//
// Both lists come from the server's `sims` / `courses` aggregations: the
// in-range one over the shared window and tenancy filter, the full one over all
// time and every available tenancy. The client send log is only a fallback for
// the in-range list, while that query loads or when it has no aggregate
// (serverRowsOrNull), since it holds the viewed tenancy alone, 13 months back
// and at most 20k rows.

import { serverRowsOrNull } from './serverScope';

const byLabel = (a, b) => a.label.localeCompare(b.label);

// Distinct, non-empty names as { label, value } options, A to Z.
export function nameOptions(names) {
    const seen = new Set();
    const out = [];
    for (const n of names || []) {
        if (n == null || n === '' || seen.has(n)) continue;
        seen.add(n);
        out.push({ label: String(n), value: n });
    }
    return out.sort(byLabel);
}

// Names on a server sims / courses response, or null while there is none, so
// callers can tell "not loaded" from "nothing sent".
export function rowNames(rows, key) {
    if (!Array.isArray(rows)) return null;
    return rows.filter((r) => r && (r.sent == null || r.sent > 0)).map((r) => r[key]);
}

// Splits the chip's options into the in-range list and the rest. A selected
// value always appears in one of the two, even when neither list has it, so it
// can still be unticked after the window moves away from it.
export function splitByRange({ inRange, all, active }) {
    const inRangeOptions = nameOptions(inRange);
    const shown = new Set(inRangeOptions.map((o) => o.value));
    const outside = nameOptions([...(all || []), ...(active || [])]).filter((o) => !shown.has(o.value));
    return { inRange: inRangeOptions, outside };
}

// Both of a chip's lists from its in-range and all-time server answers.
// fallbackNames gives the client log's names for the dates, and is only called
// when the in-range list has to come from it. The full list is held back until
// the in-range query has answered: before that, the in-range list is the client
// log, and anything sent in the dates to another tenancy would be filed as not
// sent. An empty answer still counts, so dates with nothing sent show everything
// as not sent in them rather than an empty dropdown.
export function chipLists({ inRangeRows, allRows, nameKey, inRangeScope, fallbackNames, active }) {
    const answered = rowNames(inRangeRows, nameKey);
    const inRange = serverRowsOrNull(answered, inRangeScope) ?? fallbackNames();
    const all = answered ? rowNames(allRows, nameKey) : null;
    return splitByRange({ inRange, all, active });
}

// True when the tenancy filter leaves out at least one tenancy the viewer could
// pick, so the second section's label has to mention tenancies as well as dates.
export function tenancyNarrowed(activeTenancies, availableTenancies) {
    const active = new Set((activeTenancies || []).map(Number));
    if (!active.size) return false;
    return (availableTenancies || []).some((id) => !active.has(Number(id)));
}

export function outsideLabels(narrowedByTenancy) {
    return narrowedByTenancy
        ? { label: 'Not sent to selected tenancies in these dates', emptyLabel: 'None sent to selected tenancies in these dates' }
        : { label: 'Not sent in selected dates', emptyLabel: 'None sent in selected dates' };
}
